import { Dispatch, SetStateAction } from 'react';
import { TFunction } from 'react-i18next';

import { noColumnStyle } from '../../../utils/constants/tables';
import { dateCell } from '../../../utils/helpers/dataTable';
import ActionsTableMenu from '../Components/ActionsTableMenu/ActionsTableMenu';

export type TransportEntitiesType = {
  id: number;
  createdBy?: string;
  modifiedBy?: string;
  createdAt?: string;
  modifiedAt?: string;
  volume: number;
  barcode: string;
  ownerLogisticsPoint: {
    id: number;
    name?: string;
  };
  currentLogisticsPoint: {
    id: number;
    name?: string;
  };
  destinationLogisticsPoint: {
    id: number;
    name?: string;
  };
  transportEntityType: {
    id: number;
    name?: string;
  };
};

export type TransportEntitiesTypes = {
  id: number;
  name: string;
};

export const tableStorageKey = 'transport_entities_dataTable';

export function getColumnHeadersMap(t: TFunction) {
  return {
    no: t('No.'),
    barcode: t('Barcode'),
    transportEntityType: t('Transport entity type'),
    ownerLogisticsPoint: t('Owner logistic point'),
    currentLogisticsPoint: t('Current logistic point'),
    destinationLogisticsPoint: t('Destination logistic point'),
    createdAt: t('Created At'),
    modifiedAt: t('Modified At'),
    volume: t('Volume'),
  };
}

export function additionalColumnProperties(
  t: TFunction,
  column: keyof ReturnType<typeof getColumnHeadersMap> | 'actions',
  setContextMenuSelection: Dispatch<SetStateAction<any>>,
  setAction: (t: string) => void,
  setCaller: Dispatch<SetStateAction<'context-menu' | 'group-actions'>>,
  readGuard: boolean,
  editGuard: boolean,
  deleteGuard: boolean
) {
  switch (column) {
    case 'no':
      return {
        noColumnStyle,
        style: {
          width: 30,
          maxWidth: 30,
        },
        bodyStyle: {
          width: 30,
          maxWidth: 30,
          display: 'flex',
          textAlign: 'center',
        },
      };

    case 'createdAt':
    case 'modifiedAt':
      return {
        body: (row: TransportEntitiesType) => dateCell(row[column] ?? ''),
        style: {
          width: 180,
        },
        bodyStyle: {
          width: 180,
        },
      };

    case 'volume':
      return {
        style: { width: 250 },
        bodyStyle: { width: 250 },
      };

    case 'barcode':
      return {
        style: { width: 150 },
        bodyStyle: { width: 150 },
      };

    case 'ownerLogisticsPoint':
    case 'destinationLogisticsPoint':
    case 'transportEntityType':
      return {
        body: (row: TransportEntitiesType) => row[column]?.name ?? '--',
        style: { width: 250 },
        bodyStyle: { width: 250 },
      };
    case 'currentLogisticsPoint':
      return {
        body: (row: TransportEntitiesType) =>
          row[column]?.name ?? t('transport in progress'),
        style: { width: 250 },
        bodyStyle: { width: 250 },
      };

    case 'actions':
      return {
        body: (row: TransportEntitiesType) => {
          return (
            <ActionsTableMenu
              actions={[
                {
                  label: t('View Details'),
                  icon: 'fas fa-search',
                  disabled: !readGuard,
                  command: () => {
                    setContextMenuSelection(row);
                    setAction('view-details');
                    setCaller('context-menu');
                  },
                },
                {
                  label: t('Edit'),
                  icon: 'fas fa-edit',
                  disabled: !editGuard,
                  command: () => {
                    setContextMenuSelection(row);
                    setAction('edit');
                    setCaller('context-menu');
                  },
                },
                {
                  label: t('QR Code'),
                  icon: 'fas fa-qrcode',
                  disabled: !readGuard,
                  command: () => {
                    setContextMenuSelection(row);
                    setCaller('context-menu');
                    setAction('qr-code');
                  },
                },
                {
                  label: t('Barcode'),
                  icon: 'fas fa-barcode',
                  disabled: !readGuard,
                  command: () => {
                    setContextMenuSelection(row);
                    setCaller('context-menu');
                    setAction('barcode');
                  },
                },
                {
                  label: t('Delete'),
                  icon: 'fas fa-times',
                  disabled: !deleteGuard,
                  command: () => {
                    setContextMenuSelection(row);
                    setCaller('context-menu');
                    setAction('delete');
                  },
                },
              ]}
            />
          );
        },
        style: {
          width: 60,
          borderLeft: '1px solid #e9ecef',
          flex: '0 1 auto',
          position: 'sticky',
        },
        bodyStyle: {
          width: 60,
          borderLeft: '1px solid #e9ecef',
          flex: '0 1 auto',
          position: 'sticky',
          justifyContent: 'center',
        },
      };

    default:
      return {};
  }
}
